import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Card from '../components/Card.jsx';
import Button from '../components/Button.jsx';
import { GoogleIcon } from '../components/GoogleIcon.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import './auth.css';

export default function Login() {
  const { signIn, signInWithGoogle, configured } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);

  async function handleGoogle() {
    setFormError('');
    setGoogleSubmitting(true);
    try {
      await signInWithGoogle();
      // signInWithRedirect navigates away to Google — no navigate() here.
    } catch (err) {
      setFormError(err.message);
      setGoogleSubmitting(false);
    }
  }

  function validate() {
    const errs = {};
    if (!email.trim()) errs.email = 'Enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = 'Enter a valid email.';
    if (!password) errs.password = 'Enter your password.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      const dest = location.state?.from?.pathname || '/app';
      navigate(dest, { replace: true });
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="aq-public">
      <header className="aq-public-nav">
        <Link to="/" className="aq-public-brand">
          <span className="aq-wordmark">
            Alpha<span className="aq-wordmark-accent">Qap</span>
          </span>
        </Link>
        <nav className="aq-public-nav-actions">
          <Button as={Link} to="/signup" variant="ghost" size="sm">
            Sign up
          </Button>
        </nav>
      </header>

      <div className="aq-auth-shell">
        <Card className="aq-auth-card">
          <Link to="/" className="aq-auth-back">
            ← Back to home
          </Link>
          <div>
            <h1 className="aq-auth-title heading">Log in</h1>
            <p className="aq-auth-sub">Welcome back to AlphaQap.</p>
          </div>

          {!configured ? (
            <div className="aq-auth-not-configured">
              Sign-in isn't configured on this deployment yet. The Firebase project vars
              (<code>VITE_FIREBASE_*</code>) haven't been set for this environment — see the
              README for the deploy checklist.
            </div>
          ) : (
            <div className="aq-auth-flow">
              {formError && <div className="aq-auth-alert aq-auth-alert--error">{formError}</div>}

              <Button
                type="button"
                variant="google"
                size="md"
                className="aq-auth-google"
                onClick={handleGoogle}
                disabled={googleSubmitting}
              >
                <GoogleIcon />
                {googleSubmitting ? 'Redirecting to Google…' : 'Continue with Google'}
              </Button>

              <div className="aq-auth-divider"><span>or log in with email</span></div>

              <form className="aq-auth-form" onSubmit={handleSubmit} noValidate>

              <div className={`aq-auth-field ${fieldErrors.email ? 'aq-auth-field-error' : ''}`}>
                <label htmlFor="login-email">Email</label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                />
                {fieldErrors.email && (
                  <span className="aq-auth-field-hint aq-auth-field-hint--error">{fieldErrors.email}</span>
                )}
              </div>

              <div className={`aq-auth-field ${fieldErrors.password ? 'aq-auth-field-error' : ''}`}>
                <label htmlFor="login-password">Password</label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
                {fieldErrors.password && (
                  <span className="aq-auth-field-hint aq-auth-field-hint--error">{fieldErrors.password}</span>
                )}
              </div>

              <Button type="submit" variant="primary" className="aq-auth-submit" disabled={submitting}>
                {submitting ? 'Logging in…' : 'Log in'}
              </Button>
            </form>
            </div>
          )}

          <div className="aq-auth-links">
            <Link to="/forgot-password">Forgot your password?</Link>
            <span>
              Don't have an account? <Link to="/signup">Sign up</Link>
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
}
